import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/api';
import { Client } from '../types';
import { 
  Menu, 
  X, 
  User, 
  Info,
  LogOut, 
  Home, 
  Users, 
  FileText, 
  BarChart3, 
  Settings,
  ChevronDown,
  Package,
  Target,
  Video,
  AlertTriangle,
  ClipboardCheck,
  UsersRound,
  ChevronRight,
} from 'lucide-react';
import UserImpersonation from './UserImpersonation';

type NavLink = { label: string; href: string; icon: React.ComponentType<{ className?: string }> };
type NavGroup = { label: string; icon: React.ComponentType<{ className?: string }>; children: NavLink[] };
type NavItem = NavLink | NavGroup;

const isNavGroup = (item: NavItem): item is NavGroup => 'children' in item;

const Navigation: React.FC = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [groupsDropdownOpen, setGroupsDropdownOpen] = useState(false);
  const [groupsMobileExpanded, setGroupsMobileExpanded] = useState(false);
  const [impersonationOpen, setImpersonationOpen] = useState(false);
  const [clientProfile, setClientProfile] = useState<Client | null>(null);
  const { user, isAuthenticated, logout, isLoading, isImpersonating, originalUser, stopImpersonation } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Fetch client profile for group client detection - must be called before any early returns
  useEffect(() => {
    if (isAuthenticated && user?.role === 'client') {
      // Fetch client profile to check category
      apiService.getCurrentClientProfile()
        .then(profile => setClientProfile(profile))
        .catch(err => console.error('Error fetching client profile:', err));
    }
  }, [isAuthenticated, user]);

  // Reset Groups dropdown when mobile menu closes
  useEffect(() => {
    if (!isMobileMenuOpen) {
      setGroupsMobileExpanded(false);
    }
  }, [isMobileMenuOpen]);

  // Don't render navigation while auth is loading
  if (isLoading) {
    return (
      <nav className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gray-200 rounded-lg animate-pulse"></div>
              <div className="ml-2 w-24 h-6 bg-gray-200 rounded animate-pulse"></div>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  const handleLogout = async () => {
    try {
      setIsUserMenuOpen(false);
      await logout();
      // Use replace instead of navigate to prevent back button issues
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Logout failed:', error);
      // Even if logout fails, try to navigate to home
      navigate('/', { replace: true });
    }
  };

  const isActive = (path: string) => location.pathname === path;

  const isGroupActive = (group: NavGroup) =>
    group.children.some((child) => isActive(child.href));

  const getNavItems = (): NavItem[] => {
    if (!isAuthenticated || !user) {
      return [
        { label: 'Home', href: '/', icon: Home },
        { label: 'About', href: '/about', icon: Info },
      ];
    }

    if (user.role === 'trainer') {
      return [
        { label: 'Dashboard', href: '/dashboard', icon: Home },
        { label: 'About', href: '/about', icon: Info },
        { label: 'Clients', href: '/clients', icon: Users },
        {
          label: 'Groups',
          icon: UsersRound,
          children: [
            { label: 'Plan Review', href: '/workout-plan-review', icon: ClipboardCheck },
            { label: 'Zoom Sessions', href: '/group-zoom-sessions', icon: Video },
          ],
        },
        { label: 'Check-ins', href: '/checkins', icon: FileText },
        { label: 'Materials', href: '/materials', icon: FileText },
        { label: 'Subscription Packages', href: '/subscription-packages', icon: Package },
      ];
    }

    if (user.role === 'client') {
      const isGroupClient = clientProfile?.category === 'group';
      
      if (isGroupClient) {
        // Group clients only see group-specific features
        return [
          { label: 'Dashboard', href: '/dashboard', icon: Home },
          { label: 'Training Plans', href: '/group-training-plans', icon: Target },
          { label: 'Zoom Sessions', href: '/group-zoom-sessions', icon: Video },
          { label: 'Group Guides', href: '/materials', icon: FileText },
          { label: 'About', href: '/about', icon: Info },
          { label: 'Profile', href: '/profile', icon: User },
        ];
      }
      
      // 1-on-1 clients see standard features
      return [
        { label: 'Dashboard', href: '/dashboard', icon: Home },
        { label: 'Check-in', href: '/checkin', icon: FileText },
        { label: 'My Check-ins', href: '/checkins', icon: FileText },
        { label: 'Progress', href: '/progress', icon: BarChart3 },
        { label: 'Materials', href: '/materials', icon: FileText },
        { label: 'About', href: '/about', icon: Info },
        { label: 'Profile', href: '/profile', icon: User },
      ];
    }

    return [];
  };

  const navItems = getNavItems();

  const handleStopImpersonation = async () => {
    try {
      await stopImpersonation();
    } catch (error) {
      console.error('Failed to stop impersonation:', error);
    }
  };

  return (
    <>
      {/* Impersonation Banner */}
      {isImpersonating && originalUser && (
        <div className="bg-yellow-500 text-white py-2 px-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <AlertTriangle className="w-4 h-4" />
              <span className="text-sm font-medium">
                Impersonating <strong>{user?.username}</strong> as <strong>{originalUser.username}</strong>
              </span>
            </div>
            <button
              onClick={handleStopImpersonation}
              className="text-sm font-medium hover:underline underline-offset-2"
            >
              Stop Impersonating
            </button>
          </div>
        </div>
      )}

      <nav className="bg-white shadow-sm border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between h-16">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2" aria-label="VECTOR Athletic Performance System - Home">
              <img
                src="https://lurganoidebucketire.s3.eu-north-1.amazonaws.com/unleashed/materials/logo-full.png"
                alt="VECTOR Athletic Performance System"
                className="h-8 w-auto"
              />
            </Link>
          </div>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navItems.map((item) =>
              isNavGroup(item) ? (
                <div
                  key={item.label}
                  className="relative"
                  onMouseEnter={() => setGroupsDropdownOpen(true)}
                  onMouseLeave={() => setGroupsDropdownOpen(false)}
                >
                  <button
                    type="button"
                    className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isGroupActive(item)
                        ? 'text-primary bg-primary/10'
                        : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                    }`}
                    aria-expanded={groupsDropdownOpen}
                    aria-haspopup="true"
                    aria-label={`${item.label} menu`}
                  >
                    <item.icon className="w-4 h-4" />
                    <span>{item.label}</span>
                    <ChevronDown className="w-4 h-4" />
                  </button>
                  {groupsDropdownOpen && (
                    <div className="absolute left-0 top-full pt-1 z-50">
                      <div className="bg-white rounded-md shadow-lg border border-gray-200 py-1 min-w-[180px]">
                        {item.children.map((child) => (
                          <Link
                            key={child.href}
                            to={child.href}
                            className={`flex items-center space-x-2 px-4 py-2 text-sm font-medium ${
                              isActive(child.href)
                                ? 'text-primary bg-primary/10'
                                : 'text-gray-700 hover:bg-gray-50 hover:text-primary'
                            }`}
                          >
                            <child.icon className="w-4 h-4" />
                            <span>{child.label}</span>
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`flex items-center space-x-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                    isActive(item.href)
                      ? 'text-primary bg-primary/10'
                      : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  <span>{item.label}</span>
                </Link>
              )
            )}
          </div>

          {/* User Menu / Auth Buttons */}
          <div className="flex items-center space-x-4">
            {isAuthenticated && user ? (
              <>
                <UserImpersonation
                  open={impersonationOpen}
                  onOpenChange={(open) => {
                    setImpersonationOpen(open);
                    if (!open) setIsUserMenuOpen(false);
                  }}
                />
                <div className="relative">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center space-x-2 text-sm text-gray-700 hover:text-primary focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 rounded-md px-3 py-2"
                >
                  <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                    <span className="text-secondary font-medium text-sm">
                      {user.first_name?.[0] || user.username?.[0]?.toUpperCase() || 'V'}
                    </span>
                  </div>
                  <span className="hidden sm:block">{user.first_name || user.username || 'User'}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>

                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                    <div className="px-4 py-2 text-sm text-gray-500 border-b border-gray-100">
                      {user.email || 'No email available'}
                    </div>
                    <Link
                      to="/profile"
                      className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                      onClick={() => setIsUserMenuOpen(false)}
                    >
                      <User className="w-4 h-4" />
                      <span>Profile</span>
                    </Link>
                    {((originalUser ?? user)?.is_superuser || user.role === 'trainer') && (
                      <>
                        {(originalUser ?? user)?.is_superuser && (
                          <button
                            type="button"
                            className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => {
                              setImpersonationOpen(true);
                              setIsUserMenuOpen(false);
                            }}
                          >
                            <User className="w-4 h-4" />
                            <span>Impersonate</span>
                          </button>
                        )}
                        {user.role === 'trainer' && (
                          <Link
                            to="/settings"
                            className="flex items-center space-x-2 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                            onClick={() => setIsUserMenuOpen(false)}
                          >
                            <Settings className="w-4 h-4" />
                            <span>Settings</span>
                          </Link>
                        )}
                      </>
                    )}
                    <button
                      onClick={handleLogout}
                      className="flex items-center space-x-2 w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                    >
                      <LogOut className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  </div>
                )}
                </div>
              </>
            ) : (
              <div className="flex items-center space-x-2">
                <Link
                  to="/login"
                  className="btn-primary text-sm"
                >
                  Login
                </Link>
              </div>
            )}

            {/* Mobile menu button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-primary hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary"
            >
              {isMobileMenuOpen ? (
                <X className="block h-6 w-6" />
              ) : (
                <Menu className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation */}
      {isMobileMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-200">
            {navItems.map((item) =>
              isNavGroup(item) ? (
                <div key={item.label}>
                  <button
                    type="button"
                    onClick={() => setGroupsMobileExpanded(!groupsMobileExpanded)}
                    className={`flex items-center justify-between w-full px-3 py-2 rounded-md text-base font-medium ${
                      isGroupActive(item)
                        ? 'text-primary bg-primary/10'
                        : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                    }`}
                    aria-expanded={groupsMobileExpanded}
                    aria-label={`${item.label} menu`}
                  >
                    <span className="flex items-center space-x-2">
                      <item.icon className="w-5 h-5" />
                      <span>{item.label}</span>
                    </span>
                    <ChevronRight
                      className={`w-5 h-5 transition-transform ${groupsMobileExpanded ? 'rotate-90' : ''}`}
                    />
                  </button>
                  {groupsMobileExpanded && (
                    <div className="pl-4 mt-1 space-y-1 border-l-2 border-gray-200 ml-4">
                      {item.children.map((child) => (
                        <Link
                          key={child.href}
                          to={child.href}
                          className={`flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium ${
                            isActive(child.href)
                              ? 'text-primary bg-primary/10'
                              : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                          }`}
                          onClick={() => {
                            setIsMobileMenuOpen(false);
                            setGroupsMobileExpanded(false);
                          }}
                        >
                          <child.icon className="w-5 h-5" />
                          <span>{child.label}</span>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={item.href}
                  to={item.href}
                  className={`flex items-center space-x-2 px-3 py-2 rounded-md text-base font-medium ${
                    isActive(item.href)
                      ? 'text-primary bg-primary/10'
                      : 'text-gray-600 hover:text-primary hover:bg-gray-50'
                  }`}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  <item.icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              )
            )}
          </div>
        </div>
      )}
    </nav>
    </>
  );
};

export default Navigation; 